import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Search, Clock, AlertTriangle, Users, CheckCheck, UserPlus } from 'lucide-react';
import { useSignalRConnection } from '../../../hooks/useSignalRConnection';
import PageShell from '../../../components/leaves/PageShell';
import {
  getPendingApprovals,
  approveLeave,
  rejectLeave,
  bulkApproveLeaves,
  getTeamSummary,
  getLeaveConflicts,
  getCompanyLeaveTypes,
  requestLeave,
  addLeaveAttachments,
  processCancellationApproval,
} from '../../../api/Attendance/leaves';
import { LeaveRequestView, LeaveRequestInput, LeaveBalanceView, LeaveTypeView, TeamSummaryView, ConflictView, LeaveRequestFilterParams, LeaveRequestStatus } from '../../../models/attendance';
import ApprovalQueue from './components/ApprovalQueue';
import TeamLeaveSummary from './components/TeamLeaveSummary';
import RequestDetailModal from './components/RequestDetailModal';
import BulkApprovalModal from './components/BulkApprovalModal';
import PreviousRequests from './components/PreviousRequests';
import OnBehalfEmployeePicker from './components/OnBehalfEmployeePicker';
import LeaveApplicationForm from '../MyLeaves/components/LeaveApplicationForm';
import { useWriteAccess } from '../../../hooks/useWriteAccess';
import RestrictedWriteNotice from '../../../components/subscription/RestrictedWriteNotice';
import { getApiErrorMessage } from '../../../utils/apiErrors';

const LeaveApprovals: React.FC = () => {
  const { t } = useTranslation(['leaves', 'common', 'translation', 'requests']);
  // CO-24 — approvals are /api/Leave writes, refused while the tenant is Restricted/Dormant.
  const write = useWriteAccess();
  const { subscribe } = useSignalRConnection();

  // State
  const [pendingRequests, setPendingRequests] = useState<LeaveRequestView[]>([]);
  const [teamSummary, setTeamSummary] = useState<TeamSummaryView | null>(null);
  const [conflicts, setConflicts] = useState<Record<number, ConflictView[]>>({});
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequestView | null>(null);
  const [showBulkApproval, setShowBulkApproval] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState<number[]>([]);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isTeamLoading, setIsTeamLoading] = useState(true);
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);
  const [isApprovingId, setIsApprovingId] = useState<number | null>(null);
  const [isRejectingId, setIsRejectingId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // On-behalf state
  const [showOnBehalfPicker, setShowOnBehalfPicker] = useState(false);
  const [onBehalfEmployee, setOnBehalfEmployee] = useState<{
    userId: string; fullName: string; balances: LeaveBalanceView[]; leaveTypes: LeaveTypeView[];
  } | null>(null);
  const [onBehalfSubmitting, setOnBehalfSubmitting] = useState(false);

  // Load pending approvals
  const loadPendingApprovals = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getPendingApprovals({ pageSize: 50 });
      if (result.errors.length === 0) {
        setPendingRequests(result.payload.items);
        // Load conflicts for each request
        const conflictMap: Record<number, ConflictView[]> = {};
        await Promise.all(
          result.payload.items.map(async (req) => {
            try {
              const conflictResult = await getLeaveConflicts(req.id);
              if (conflictResult.errors.length === 0) {
                conflictMap[req.id] = conflictResult.payload;
              }
            } catch { /* ignore individual failures */ }
          })
        );
        setConflicts(conflictMap);
      }
    } catch { /* ignore */ }
    setIsLoading(false);
  }, []);

  // Load team summary
  const loadTeamSummary = useCallback(async () => {
    setIsTeamLoading(true);
    try {
      const result = await getTeamSummary();
      if (result.errors.length === 0) {
        setTeamSummary(result.payload);
      }
    } catch { /* ignore */ }
    setIsTeamLoading(false);
  }, []);

  // Load leave types for filter dropdown and color badges
  const loadLeaveTypes = useCallback(async () => {
    try {
      const result = await getCompanyLeaveTypes();
      if (result.errors.length === 0) {
        setLeaveTypes(result.payload);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadPendingApprovals();
    loadTeamSummary();
    loadLeaveTypes();
  }, [loadPendingApprovals, loadTeamSummary, loadLeaveTypes]);

  // SignalR real-time updates
  useEffect(() => {
    const unsub1 = subscribe('LeaveApproved', () => { loadPendingApprovals(); loadTeamSummary(); });
    const unsub2 = subscribe('LeaveRejected', () => { loadPendingApprovals(); loadTeamSummary(); });
    const unsub3 = subscribe('LeaveUndone', () => { loadPendingApprovals(); loadTeamSummary(); });
    const unsub4 = subscribe('LeaveCancellationApproved', () => { loadPendingApprovals(); loadTeamSummary(); });
    const unsub5 = subscribe('LeaveCancellationRejected', () => { loadPendingApprovals(); loadTeamSummary(); });
    return () => { unsub1(); unsub2(); unsub3(); unsub4(); unsub5(); };
  }, [subscribe, loadPendingApprovals, loadTeamSummary]);

  // Toast helper
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Filter pending requests
  const filteredRequests = pendingRequests.filter((request) => {
    const matchesSearch =
      !searchTerm ||
      request.user?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      request.comments?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || request.leaveType?.toLowerCase() === filterType.toLowerCase();
    return matchesSearch && matchesType;
  });

  // Stats
  const stats = {
    totalPending: pendingRequests.length,
    withConflicts: Object.values(conflicts).filter(c => c.length > 0).length,
    teamOnLeave: teamSummary?.onLeaveToday || 0,
  };

  // Action handlers
  //
  // CO-346 — THE REFUSAL MUST REACH THE USER, AND THE CALLER MUST LEARN IT WAS REFUSED.
  //
  // The response interceptor REJECTS every non-2xx, so a server refusal (the workflow gate's
  // `PermissionDenied`, an attachment-required 400, a stale row) arrives in the `catch` and NEVER in
  // the resolved branch — the `result.errors.length === 0` else-branch below is unreachable for a
  // 4xx and is kept only for a hypothetical 200-with-errors body. `getApiErrorMessage` is the
  // repo-wide reader: it delegates to `apiErrorsOf`, which walks the rejection wrappers and the
  // non-enumerable `__apiErrors` stamp, then reads `description` (NOT `message` — the server `Error`
  // model has no `message`) and falls back to the translated string passed here.
  //
  // Both handlers return whether the server ACCEPTED the decision, because the detail modal used to
  // close unconditionally on return: a refused approval dismissed the modal and dropped the row from
  // sight exactly like a successful one.
  const handleApproveRequest = async (requestId: number, comments: string = ''): Promise<boolean> => {
    setIsApprovingId(requestId);
    let accepted = false;
    try {
      const req = pendingRequests.find(r => r.id === requestId);
      const isCancellation = req?.status === LeaveRequestStatus.CancellationPending;
      const result = isCancellation
        ? await processCancellationApproval(requestId, true, comments)
        : await approveLeave(requestId, comments);
      if (result.errors.length === 0) {
        accepted = true;
        showToast(t('Leave Approval'), 'success');
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
        setSelectedRequests(prev => prev.filter(id => id !== requestId));
        loadTeamSummary();
      } else {
        showToast(getApiErrorMessage(result, t, t('Submission failed')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Submission failed')), 'error');
      // The queue's own view of who may decide what just proved wrong (or the row moved on), so
      // re-read it rather than leaving a button that promises something the server refuses.
      loadPendingApprovals();
    }
    setIsApprovingId(null);
    return accepted;
  };

  const handleRejectRequest = async (requestId: number, comments: string = ''): Promise<boolean> => {
    setIsRejectingId(requestId);
    let accepted = false;
    try {
      const req = pendingRequests.find(r => r.id === requestId);
      const isCancellation = req?.status === LeaveRequestStatus.CancellationPending;
      const result = isCancellation
        ? await processCancellationApproval(requestId, false, comments)
        : await rejectLeave(requestId, comments);
      if (result.errors.length === 0) {
        accepted = true;
        showToast(isCancellation ? t('cancellationProcessed', 'Cancellation processed') : t('Leave Rejection'), 'success');
        setPendingRequests(prev => prev.filter(r => r.id !== requestId));
        setSelectedRequests(prev => prev.filter(id => id !== requestId));
        loadTeamSummary();
      } else {
        showToast(getApiErrorMessage(result, t, t('Submission failed')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Submission failed')), 'error');
      loadPendingApprovals();
    }
    setIsRejectingId(null);
    return accepted;
  };

  /**
   * Bulk approve. A cancellation request is a different decision on a different
   * endpoint (`Leave/CancellationApproval`), so posting it to `Leave/BulkApprove`
   * is a silent no-op. The queue already blocks selecting one (the row checkbox
   * is disabled with a reason), and this partition is the second line of defence
   * for any id that reaches here another way — it routes cancellations through
   * their own endpoint instead of dropping them.
   *
   * The result is reported per id rather than as an unconditional success toast:
   * `Leave/BulkApprove` returns a count, so a partial failure used to look
   * identical to a clean run.
   */
  const handleBulkApproval = async (requestIds: number[], comments: string = '') => {
    if (!requestIds.length) return;
    const isCancellation = (id: number) =>
      pendingRequests.find(r => r.id === id)?.status === LeaveRequestStatus.CancellationPending;
    const cancellationIds = requestIds.filter(isCancellation);
    const standardIds = requestIds.filter(id => !isCancellation(id));

    let approved = 0;
    let firstError: string | null = null;

    try {
      if (standardIds.length > 0) {
        const result = await bulkApproveLeaves(standardIds, comments);
        if (result.errors.length === 0) {
          // Today the payload is the approved count. Tolerate a richer per-id
          // shape without assuming it, so a server change cannot mis-report.
          const payload = result.payload as unknown;
          approved += typeof payload === 'number' ? payload : standardIds.length;
        } else {
          // `description`, never `message` — the server `Error` model has no `message` field, so the
          // old read was always `undefined` and every partial failure showed the generic fallback.
          firstError = getApiErrorMessage(result, t, t('Submission failed'));
        }
      }
      for (const id of cancellationIds) {
        try {
          const result = await processCancellationApproval(id, true, comments);
          if (result.errors.length === 0) approved += 1;
          else if (!firstError) firstError = getApiErrorMessage(result, t, t('Submission failed'));
        } catch (err) {
          if (!firstError) firstError = getApiErrorMessage(err, t, t('Submission failed'));
        }
      }
    } catch (err) {
      firstError = getApiErrorMessage(err, t, t('Submission failed'));
    }

    if (approved === requestIds.length) {
      showToast(t('Leave Approval'), 'success');
    } else if (approved > 0) {
      showToast(
        t('requests:{{n}} of {{total}} approved', { n: approved, total: requestIds.length }),
        'error',
      );
    } else {
      showToast(firstError || t('Submission failed'), 'error');
    }

    setSelectedRequests([]);
    setShowBulkApproval(false);
    // The server is the only source of truth about which rows actually settled.
    loadPendingApprovals();
    loadTeamSummary();
  };

  const handleRequestSelection = (requestId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedRequests(prev => [...prev, requestId]);
    } else {
      setSelectedRequests(prev => prev.filter(id => id !== requestId));
    }
  };

  const handleQuickApprove = (requestId: number) => {
    handleApproveRequest(requestId);
  };

  const handleQuickReject = (requestId: number) => {
    // For quick reject, open the modal so user can add comments
    const req = pendingRequests.find(r => r.id === requestId);
    if (req) setSelectedRequest(req);
  };

  // On-behalf handlers
  const handleOnBehalfEmployeeSelected = (employee: {
    userId: string; fullName: string; balances: LeaveBalanceView[]; leaveTypes: LeaveTypeView[];
  }) => {
    setShowOnBehalfPicker(false);
    setOnBehalfEmployee(employee);
  };

  const handleOnBehalfSubmit = async (input: LeaveRequestInput, files: File[]) => {
    setOnBehalfSubmitting(true);
    try {
      const result = await requestLeave(input);
      if (result.errors.length === 0) {
        if (files.length > 0) {
          await addLeaveAttachments({
            leaveRequestId: result.payload.id,
            attachments: files,
          });
        }
        showToast(
          t('onBehalfSubmitSuccess', { name: onBehalfEmployee?.fullName }),
          'success'
        );
        setOnBehalfEmployee(null);
        loadPendingApprovals();
        loadTeamSummary();
      } else {
        showToast(getApiErrorMessage(result, t, t('Submission failed')), 'error');
      }
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('Submission failed')), 'error');
    }
    setOnBehalfSubmitting(false);
  };

  return (
    <PageShell
      title={t('Leave Approvals')}
      description={t('translation:leaveApprovalsDescription', 'Manage leave requests, approve applications, and monitor team coverage')}
      icon={CheckCircle}
      accentColor="emerald"
      stats={[
        { label: t('translation:pending', 'Pending'), value: stats.totalPending, color: 'text-orange-600' },
        { label: t('translation:withConflicts', 'With Conflicts'), value: stats.withConflicts, color: 'text-yellow-600' },
        { label: t('translation:onLeave', 'On Leave'), value: stats.teamOnLeave, color: 'text-blue-600' },
      ]}
      className="min-h-screen bg-background"
    >
      <RestrictedWriteNotice className="mt-6" />

      {/* Filters and Search */}
      <div className="py-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search size={20} className="absolute ltr:left-3 rtl:right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder={t('translation:searchPlaceholder', 'Search employees...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="approval-search"
              className="w-full ltr:pl-10 ltr:pr-4 rtl:pr-10 rtl:pl-4 py-2 bg-card border border-border rounded-lg text-foreground placeholder-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            />
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              data-testid="approval-type-filter"
              className="px-3 py-2 bg-card border border-border rounded-lg text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            >
              <option value="all">{t('translation:allTypes', 'All Types')}</option>
              {leaveTypes.filter(lt => lt.isActive).map(lt => (
                <option key={lt.id} value={lt.name.toLowerCase()}>{lt.name}</option>
              ))}
            </select>

            {/* Submit on Behalf */}
            <button
              onClick={() => setShowOnBehalfPicker(true)}
              data-testid="on-behalf-btn"
              {...write.buttonProps}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-blue-600"
            >
              <UserPlus size={16} className="ltr:mr-2 rtl:ml-2" />
              {t('Submit on Behalf')}
            </button>

            {/* Bulk Actions */}
            {selectedRequests.length > 0 && (
              <button
                onClick={() => setShowBulkApproval(true)}
                data-testid="bulk-approve-btn"
                {...write.buttonProps}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-green-600"
              >
                <CheckCheck size={16} className="ltr:mr-2 rtl:ml-2" />
                {t('translation:bulkApprove', 'Bulk Approve')} ({selectedRequests.length})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Approval Queue */}
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-xl">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground flex items-center">
                  <Clock size={20} className="ltr:mr-2 rtl:ml-2" />
                  {t('translation:approvalQueue', 'Approval Queue')}
                  <span className="ltr:ml-2 rtl:mr-2 px-2 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
                    {filteredRequests.length}
                  </span>
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('translation:approvalQueueDesc', 'Pending leave requests requiring your approval')}
                </p>
              </div>
              <div className="p-6">
                {isLoading ? (
                  <div className="space-y-4 animate-pulse">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="border border-border rounded-lg p-4">
                        <div className="flex items-center space-x-3 rtl:space-x-reverse mb-3">
                          <div className="w-12 h-12 bg-muted rounded-full" />
                          <div className="space-y-2">
                            <div className="h-4 w-32 bg-muted rounded" />
                            <div className="h-3 w-24 bg-muted rounded" />
                          </div>
                        </div>
                        <div className="h-16 bg-muted rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <ApprovalQueue
                    requests={filteredRequests}
                    conflicts={conflicts}
                    leaveTypes={leaveTypes}
                    onViewRequest={setSelectedRequest}
                    onApproveRequest={handleQuickApprove}
                    onRejectRequest={handleQuickReject}
                    onRequestSelection={handleRequestSelection}
                    selectedRequests={selectedRequests}
                    isApproving={isApprovingId}
                    isRejecting={isRejectingId}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Team Leave Summary */}
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl">
              <div className="p-6 border-b border-border">
                <h2 className="text-xl font-semibold text-foreground flex items-center">
                  <Users size={20} className="ltr:mr-2 rtl:ml-2" />
                  {t('translation:teamLeaveSummary', 'Team Leave Summary')}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('translation:teamLeaveSummaryDesc', 'Current and upcoming team leaves')}
                </p>
              </div>
              <div className="p-6">
                <TeamLeaveSummary teamSummary={teamSummary} isLoading={isTeamLoading} leaveTypes={leaveTypes} />
              </div>
            </div>
          </div>
        </div>

        {/* Previous Leave Requests Table */}
        <div className="mt-8">
          <PreviousRequests leaveTypes={leaveTypes} />
        </div>
      </div>

      {/* Request Details Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          leaveTypes={leaveTypes}
          onClose={() => setSelectedRequest(null)}
          onApprove={handleApproveRequest}
          onReject={handleRejectRequest}
        />
      )}

      {/* Bulk Approval Modal */}
      {showBulkApproval && (
        <BulkApprovalModal
          requestCount={selectedRequests.length}
          selectedRequestIds={selectedRequests}
          onClose={() => setShowBulkApproval(false)}
          onConfirm={handleBulkApproval}
        />
      )}

      {/* On-Behalf Employee Picker */}
      {showOnBehalfPicker && (
        <OnBehalfEmployeePicker
          onSelect={handleOnBehalfEmployeeSelected}
          onCancel={() => setShowOnBehalfPicker(false)}
        />
      )}

      {/* On-Behalf Leave Application Form */}
      {onBehalfEmployee && (
        <LeaveApplicationForm
          onSubmit={handleOnBehalfSubmit}
          onCancel={() => setOnBehalfEmployee(null)}
          balances={onBehalfEmployee.balances}
          leaveTypes={onBehalfEmployee.leaveTypes}
          userId={onBehalfEmployee.userId}
          submitting={onBehalfSubmitting}
          onBehalfEmployeeName={onBehalfEmployee.fullName}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2 rtl:space-x-reverse ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
          <span>{toast.message}</span>
        </div>
      )}
    </PageShell>
  );
};

export default LeaveApprovals;
